import { isPlatformBrowser } from "@angular/common";
import { Component, DestroyRef, Inject, OnInit, PLATFORM_ID } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { EAppEventName } from "@core-constants/app-events.const";
import { PaymentsDataService } from "@core-data-services/payments.data-service";
import { BroadcastService } from "@shared-services/broadcast.service";
import { IRedeemInitiatives, IRedeemInitiativesResponse } from "@core-models/payments.model";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateService } from "@shared-services/translate.service";
import { RouteHelper } from "@core-helpers/route.helper";
import { ContactInfoHelper, DomainContactInfo, DomainContactItem, IContact } from "@core-models/contacts.model";
import { CheckoutManager } from "@core-managers/checkout.manager";
import { ContactsDataService } from "@core-data-services/contacts.data-service";
import { EContactType } from "@core-constants/contact-data.const";
import { Tools } from "@shared-utils/tools.util";
import { AppClientContext } from "@shared-base/client-context.base";
import { RedeemManager } from "@core-managers/redeem.manager";
import { ToastService } from "@shared-services/toast.service";
import { HttpErrorResponse } from "@angular/common/http";
import { EInitiatives, EStatusInitiatives, Initiatives } from "@core-constants/initiatives.const";
import { IInitiative } from "@shared-base/generic-clases.base";
import { ErrorMessageConst } from "@core-constants/error-message.const";
import { AssetsService } from "@shared-services/assets.service";

@Component({
  selector: 'app-initiatives-data',
  templateUrl: './initiatives-data.component.html',
  styleUrls: ['./initiatives-data.component.css']
})
export class InitiativesDataComponent implements OnInit
{
  public acceptedPolicies: boolean = false;
  public couponId: string | null = null;
  public domain: string | null = null;
  public status: string | null = null;

  public initiativeId: string | null = null;
  public showConfirmationModal: boolean = false;
  public paymentOrderId: string | null = null;
  public dudaSetupUrl: string | null = null;
  public showButtonRedeem: boolean = false;

  public contacts: IContact[];
  public redisDomainContacts: DomainContactInfo | undefined;
  public redisContactsReponse: DomainContactInfo | undefined;
  public registrantContactId: number | undefined = undefined;
  public allowShareContactInfo: boolean = false;
  public allowShareRegistrantInfo: boolean = false;
  public isRedisContactsLoaded: boolean = false;
  public selectedContactId: number | undefined = undefined;
  public isShowPersonTypeOptions: boolean = true;
  public activeContactTab: number = 0;
  public areTermsChecked: boolean = false;
  public isContactsDomainChecked: boolean = true;
  public showContactForm: boolean = false;
  public isEnabledButtonContinue: boolean = true;
  public editContact: any[];
  public selectedInitiative: IInitiative | undefined;

  constructor(@Inject(PLATFORM_ID) private platformId,
    private titleService: Title,
    public translateService: TranslateService,
    protected paymentsDataService: PaymentsDataService,
    private activatedRoute: ActivatedRoute,
    private routeHelper: RouteHelper,
    protected checkoutManager: CheckoutManager,
    protected redeemManager: RedeemManager,
    protected contactsDataService: ContactsDataService,
    private assetsService: AssetsService,
    protected toast: ToastService,
    private destroyRef$: DestroyRef
  ) { }

  public ngOnInit(): void
  {
    if (isPlatformBrowser(this.platformId))
    {
      this.couponId = this.redeemManager.token;
      this.domain = this.redeemManager.domain;
      this.status = this.redeemManager.status;
      this.initiativeId = this.redeemManager.initiativeId;

      this.loadSelectedInitiative();
      this.redeemManager.checkLogin();
      this.isVisibleButtonRedeem();
    }

    this.registerEventListeners();
    this.registerLanguageEventListeners();
  }

  private loadSelectedInitiative(): void
  {
    const baseInitiative = Initiatives.find(item => item.id === (this.redeemManager.initiativeId as unknown as EInitiatives));

    const pathBlob = this.assetsService.getAssetFromBlob("");
    const lang = this.translateService.languageCode.toLocaleLowerCase();

    if (!baseInitiative)
    {
      return;
    }

    this.selectedInitiative = {
      ...baseInitiative,
      title: this.translateService.getElement(
        baseInitiative.title,
        pathBlob,
        lang,
        pathBlob,
        lang
      )
    };

    this.titleService.setTitle(`${this.translateService.getElement(baseInitiative.label)} | Akky`);
  }

  private registerLanguageEventListeners(): void
  {
    this.translateService.onTranslateChange()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: () => this.loadSelectedInitiative()
      });
  }

  public registerEventListeners(): void
  {
    BroadcastService.Instance.on(EAppEventName.OnLoginSuccess)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: () =>
        {
          this.redeemManager.checkLogin();
        }
      });

    BroadcastService.Instance.on(EAppEventName.OnUserLogout)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: () =>
        {
          this.redeemManager.checkLogin();
        }
      });

    this.activatedRoute.data.subscribe({
      next: (response: any) =>
      {
        this.contacts = [];
        if (!Tools.isNullOrEmpty(response.data))
        {
          this.contacts = response?.data;
          const selected: any = this.checkoutManager.registrantContact != undefined ? this.checkoutManager.registrantContact : this.contacts.find(x => x.hasBeenUsedAsRegistrant);

          // all flags are set with regristant contact because is the default status
          this.registrantContactId = selected ? selected.id : undefined;
        }
      }
    });

    this.contactsDataService.getRedisContactDomain().subscribe({
      next: (response: DomainContactInfo) =>
      {
        if (response)
        {
          this.isContactsDomainChecked = response.isCheckedDomainContacts;
          this.allowShareContactInfo = response.publishContactInfo;
          this.allowShareRegistrantInfo = response.publishRegistrantInfo;

          const contacts = response.domainContacts;

          if (!this.isContactsDomainChecked && contacts && !Tools.isNullOrEmpty(contacts))
          {
            this.redisContactsReponse = new DomainContactInfo();
            this.redisContactsReponse = response;

            this.isRedisContactsLoaded = true;
            const registrantId = contacts.find(x => x.contactType == EContactType.Registrant)?.id;
            registrantId ? this.onSelectRegistrantContact(registrantId, true) : undefined;
          }
          else
          {
            this.isRedisContactsLoaded = true;
            const redisRegistrant = contacts.find(x => x.contactType == EContactType.Registrant).id;
            redisRegistrant ? this.onSelectRegistrantContact(redisRegistrant, true) : undefined;
          }
        }
        else
        {
          this.isRedisContactsLoaded = false;
        }
      }
    });

  }

  public redeem(): void
  {
    if (!isPlatformBrowser(this.platformId))
    {
      return;
    }

    if (!this.redeemManager.isUserLoggedIn)
    {
      BroadcastService.Instance.broadcast(EAppEventName.OnOpenLogin);
      return;
    }

    if (!this.acceptedPolicies)
    {
      this.toast.setErrorToast(this.translateService.getElement("Lee y acepta nuestras políticas"));
      return;
    }

    if (this.registrantContactId == 0 || !this.registrantContactId || !this.isContactsDomainChecked )
    {
      this.toast.setErrorToast(ErrorMessageConst.RegistrantRequired);
      return;
    }

    const contact: DomainContactItem = this.newRedisContact(this.registrantContactId.toString(), EContactType.Registrant);
    const dto: IRedeemInitiatives = {
      domainName: this.domain,
      initiativeId: this.initiativeId,
      domainContact: contact,
      userId: this.redeemManager.userId
    };

    const random = Math.random().toString().substring(2);
    const dataServiceTokenName = (<any>this.constructor).name + "_" + random;
    AppClientContext.Instance.setDataServiceBusy(dataServiceTokenName);

    this.paymentsDataService.redeemInitiatives(dto)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: (response: IRedeemInitiativesResponse) =>
        {
          this.paymentOrderId = response.paymentOrderId;
          this.dudaSetupUrl = response.dudaSetupUrl;
          this.showConfirmationModal = true;
          this.redeemManager.status = EStatusInitiatives.Finished;
          this.showButtonRedeem = false;

          AppClientContext.Instance.setDataServiceDone(dataServiceTokenName);
        },
        error: (response: HttpErrorResponse) =>
        {
          try
          {
            if (response?.error?.message === null && response?.status === 404)
            {
              this.toast.setErrorToast(this.translateService.getElement("Ocurrió un error al reclamar tus beneficios."));
            }
            else
            {
              this.toast.setErrorToast(response?.error?.message);
            }
          }
          finally
          {
            AppClientContext.Instance.setDataServiceDone(dataServiceTokenName);
          }
        }
      });
  }

  public get noticeOfPrivacyURL(): string
  {
    return `${this.routeHelper.noticeOfPrivacyURL}?lang=${this.translateService.languageCode}`;
  }

  public onGoToEditSiteClick(): void
  {
    if (isPlatformBrowser(this.platformId))
    {
      window.open(this.dudaSetupUrl, "_blank");
    }
  }

  public isVisibleButtonRedeem(): void
  {
    if (!(this.status === EStatusInitiatives.Finished))
    {
      this.showButtonRedeem = true;
    }
    else
    {
      this.showButtonRedeem = false;
    }
  }
  // ********************************************************************
  //#region CONTACTS
  // ********************************************************************

  public onSelectRegistrantContact($event, isRedisResponse: boolean): void
  {
    this.registrantContactId = $event;
    this.checkoutManager.registrantContact = this.createContactHelperFromContact(this.registrantContactId);
    if (!isRedisResponse)
    {
      this.setRedisContact($event, EContactType.Registrant);
    }
  }

  public createContactHelperFromContact(id: any): ContactInfoHelper
  {
    const contact: IContact = this.contacts?.find(x => x.id == id);
    let contactInfo: ContactInfoHelper = undefined;

    if (contact)
    {
      contactInfo = {
        id: +contact.id,
        name: contact.name,
        contactPersonType: +(contact.idContactPersonType),
        hasBeenUsedAsRegistrant: contact.hasBeenUsedAsRegistrant
      };
    }

    return contactInfo;
  }


  public onShowContactFormClick($event, showOption: boolean, tab: number): void
  {
    this.selectedContactId = $event;
    this.showContactForm = true;
    this.isShowPersonTypeOptions = showOption;
    this.activeContactTab = tab;
  }

  public onContactsUpdated($event: any): void
  {
    this.showContactForm = false;

    this.contactsDataService.getList().subscribe({
      next: (res: any) =>
      {
        if ($event)
        {
          this.contacts = res;
          const id = res.reduce((prev, current) => ((prev.id > current.id) ? prev : current), 0).id;
          switch (this.activeContactTab)
          {
            default:
              this.registrantContactId = id;
              break;
          }
          this.setData();
        }
        else
        {
          this.contacts = res;
        }
      }
    });
  }

  public setData(): void
  {
    const registrant = this.createContactHelperFromContact(this.registrantContactId);
    this.checkoutManager.registrantContact = registrant;
  }


  public setRedisContact(idcontact: any, typeContact: EContactType): void
  {
    if (this.registrantContactId == undefined)
    {
      return;
    }

    if (this.isContactsDomainChecked)
    {
      const registrant = this.contacts.find(x => x.id == idcontact);

      /* CREATE CONTACTS ONLY WITH REGISTRANT */
      this.redisDomainContacts = new DomainContactInfo();
      this.redisDomainContacts.domainContacts = [];

      for (let i = 1; i <= 4; i++)
      {
        if (registrant != undefined)
        {
          const redisContact = this.newRedisContact(registrant.id, i);
          this.redisDomainContacts.domainContacts.push(redisContact);
        }
      }
    }
    else
    {
      /* CREATE CONTACT WITH DOMAIN CONTACTS */
      if (this.redisDomainContacts == undefined)
      {
        this.redisDomainContacts = new DomainContactInfo();
        this.redisDomainContacts.domainContacts = [];
      }
      const redisContact = this.newRedisContact(idcontact, typeContact);

      const wasAdded = this.redisDomainContacts.domainContacts.find(x => x.contactType == typeContact);

      if (wasAdded == undefined)
      {
        this.redisDomainContacts.domainContacts.push(redisContact);
      }
      else
      {
        /* REMOVE DUPLICATES */
        this.redisDomainContacts.domainContacts.map((x, index) =>
        {
          if (x.contactType == typeContact)
          {
            this.redisDomainContacts?.domainContacts.splice(index, 1);
          }
        });
        this.redisDomainContacts.domainContacts.push(redisContact);
      }
    }

    this.redisDomainContacts.publishRegistrantInfo = this.allowShareRegistrantInfo;
    this.redisDomainContacts.publishContactInfo = this.allowShareContactInfo;
    this.redisDomainContacts.isCheckedDomainContacts = this.isContactsDomainChecked;

    if (this.isRedisContactsLoaded && this.redisDomainContacts.domainContacts.length && this.redisContactsReponse && !this.isContactsDomainChecked)
    {
      /* apply latest changes when refresh the page and change contacts */
      this.redisContactsReponse.domainContacts = [...this.redisContactsReponse.domainContacts, ...this.redisDomainContacts.domainContacts];

      const arrayContacts: any = this.redisContactsReponse.domainContacts.map(item =>
      {
        return [item.contactType, item];
      });

      const dictionaryContacts = new Map(arrayContacts);

      const newContactsArray = [...dictionaryContacts.values()];

      this.redisContactsReponse.domainContacts = newContactsArray as DomainContactItem[];

      this.redisContactsReponse.isCheckedDomainContacts = this.isContactsDomainChecked;
      this.redisContactsReponse.publishContactInfo = this.allowShareContactInfo;
      this.redisContactsReponse.publishRegistrantInfo = this.allowShareRegistrantInfo;

      if (this.registrantContactId)
      {
        const hasRegistrant = this.redisContactsReponse.domainContacts.some(x => x.contactType == EContactType.Registrant);

        if (!hasRegistrant)
        {
          const redisRegistrant = this.newRedisContact(this.registrantContactId.toString(), EContactType.Registrant);

          this.redisContactsReponse.domainContacts.push(redisRegistrant);
        }
      }
      this.onSaveRedisContact(this.redisContactsReponse);
    }

    if (this.redisDomainContacts.domainContacts.length && !this.redisContactsReponse && !this.isContactsDomainChecked)
    {
      this.onSaveRedisContact(this.redisDomainContacts);
    }

    if (this.redisDomainContacts.domainContacts.length && this.isContactsDomainChecked)
    {
      this.onSaveRedisContact(this.redisDomainContacts);
    }
  }

  public newRedisContact(id: string, conType: EContactType): DomainContactItem
  {
    const dataContact = this.contacts.find(x => x.id == id);

    if (dataContact)
    {
      const newRedisConact: DomainContactItem = new DomainContactItem();
      newRedisConact.id = id;
      newRedisConact.name = dataContact.name;
      newRedisConact.contactPersonType = +dataContact.idContactPersonType;
      newRedisConact.hasBeenUsedAsRegistrant = dataContact.hasBeenUsedAsRegistrant;
      newRedisConact.contactType = conType;

      return newRedisConact;
    }
  }

  public onSaveRedisContact(redisToSave: DomainContactInfo): void
  {
    this.contactsDataService.addRedisContactDomain(redisToSave).subscribe({
      next: () => { }
    });
  }
  // #endregion
}
